// src/components/AddWeeklyAds.js
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs, Timestamp } from "firebase/firestore";
import Cache from "../utils/cache";
import Select from "react-select";

export default function AddWeeklyAds() {
  const [vendorList, setVendorList] = useState([]);
  const [vendor, setVendor] = useState("");
  const [offerLink, setOfferLink] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(""); // yyyy-mm-dd string
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Load vendors for dropdown on mount with Cache usage
  useEffect(() => {
    const loadVendors = async () => {
      let vendorList = [];

      const cached = Cache.getData();
      const cachedVendors = cached?.vendors;

      if (
        cachedVendors &&
        Array.isArray(cachedVendors) &&
        cachedVendors.length > 0 &&
        typeof cachedVendors[0] === "object"
      ) { 
        vendorList = cachedVendors.map((v) => v.vendorName || v.id).filter(Boolean);
      } else {
        try {
          const snapshot = await getDocs(collection(db, "vendors"));
          const groupedDocs = snapshot.docs.filter((doc) =>
            doc.id.startsWith("vendors_")
          );

          const allVendors = [];
          groupedDocs.forEach((doc) => {
            const data = doc.data();
            if (Array.isArray(data.vendors)) {
              data.vendors.forEach((vendor) => {
                if (!vendor.vendorName && vendor.id) {
                  vendor.vendorName = vendor.id;
                }
                allVendors.push(vendor);
              });
            }
          });

          vendorList = allVendors.map((v) => v.vendorName || v.id).filter(Boolean);

          // Update cache
          Cache.setData({
            ...cached,
            vendors: allVendors,
          });
        } catch (error) {
          console.error("❌ Failed to load vendors:", error);
        }
      }

      vendorList = vendorList.sort((a, b) => a.localeCompare(b));
      const options = vendorList.map((v) => ({ label: v, value: v }));
      setVendorList(options);
      setVendor("");

    };

    loadVendors();
  }, []);


  // Helper to convert yyyy-mm-dd to Firestore Timestamp with time set to 12:00 AM
  const toStartTimestamp = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return Timestamp.fromDate(d);
  };

  // Helper to convert yyyy-mm-dd to Firestore Timestamp with time set to 11:59:59.999 PM
  const toEndTimestamp = (dateStr) => {
    const d = new Date(dateStr + "T23:59:59.999");
    return Timestamp.fromDate(d);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    // Basic validation
    if (!vendor.trim()) {
      setMessage("Please select a vendor.");
      return;
    }
    if (!offerLink.trim()) {
      setMessage("Please enter the offer link URL.");
      return;
    }
    if (!startDate) {
      setMessage("Please select the start date.");
      return;
    }
    if (!endDate) {
      setMessage("Please select the end date.");
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setMessage("End date cannot be before start date.");
      return;
    }

    setLoading(true);

    try {
      await addDoc(collection(db, "weekly_ads"), {
        vendor: vendor.trim(),
        offerLink: offerLink.trim(),
        description: description.trim(),
        startDate: toStartTimestamp(startDate),
        endDate: toEndTimestamp(endDate),
        uploadedAt: Timestamp.now(),
      });
      setMessage("Weekly ad uploaded successfully!");
      setOfferLink("");
      setDescription("");
      setStartDate("");
      setEndDate("");
      // Keep vendor selected for convenience
    } catch (error) {
      setMessage("Error uploading weekly ad: " + error.message);
    }

    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded shadow">
      <h2 className="text-2xl font-semibold mb-4 text-center">Upload Weekly Ad</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col">
  Vendor:
  <div className="mt-1">
    <Select
      options={vendorList}
      value={vendorList.find((v) => v.value === vendor) || null}
      onChange={(selected) => setVendor(selected.value)}
      placeholder="Select Vendor"
      isSearchable
    />
  </div>
</label>



        <label className="flex flex-col">
          Offer Link (URL):
          <input
            type="url"
            value={offerLink}
            onChange={(e) => setOfferLink(e.target.value)}
            className="border rounded p-2 mt-1"
            placeholder="https://example.com/weekly-ad"
            required
          />
        </label>

        <label className="flex flex-col">
          Description:
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="border rounded p-2 mt-1"
            placeholder="Optional description"
            rows={3}
          />
        </label>

        <label className="flex flex-col">
          Start Date:
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border rounded p-2 mt-1"
            required
          />
        </label>

        <label className="flex flex-col">
          End Date:
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border rounded p-2 mt-1"
            required
          />
        </label>

        <button
          type="submit"
          disabled={loading}
          className="bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 disabled:opacity-50 transition"
        >
          {loading ? "Uploading..." : "Upload Weekly Ad"}
        </button>
      </form>

      {message && <p className="mt-4 text-center text-sm text-red-600">{message}</p>}
    </div>
  );
}
